import { RigModel, hsl, idHash, scale, type Animator, type Gait, type Palette, type Part, type PathOp, type Pose, type Shape } from "./Rig";
import { pixelModel } from "./pixelArt";

/**
 * A mossback: a low six-legged shell with moss and a flower growing on it,
 * a blunt head poking out the front that dips to graze. Slow. Feet at the
 * origin, up is +y, it faces +x.
 */

const legs: Part[] = Array.from({ length: 6 }, (_, i) => {
  const far = i >= 3;
  const x = 0.95 - (i % 3) * 0.95 + (far ? 0.25 : 0);
  // The hip sits up inside the body, so the leg can swing without a corner showing.
  return { name: `leg${i}`, parent: "root", at: [x, 0.55], z: far ? i - 3 : 4 + (i % 3), moves: true, shapes: [{ color: far ? "far" : "skin", form: { kind: "capsule", to: [0, -0.5], width: 0.28 } }] };
});

const shellLines: PathOp[] = [-0.9, -0.3, 0.3, 0.9].flatMap((k): PathOp[] => [["M", k, 0.72], ["Q", k * 0.8, 1.3, k * 0.5, 1.56]]);
shellLines.push(["M", -1.3, 1.15], ["Q", 0, 1.3, 1.3, 1.15]);
const moss: Shape[] = Array.from({ length: 7 }, (_, i) => ({ color: "moss", at: [-1.1 + i * 0.36, 1.45 + Math.sin(i * 1.3) * 0.12], form: { kind: "disc", r: 0.26, ry: 0.16 } }));

export const MOSSBACK_MODEL = pixelModel({
  parts: [
    { name: "root", at: [0, 0], z: 0, moves: true, shapes: [] },
    ...legs,
    {
      name: "body",
      parent: "root",
      at: [0, 0],
      z: 3,
      shapes: [
        { color: "skin", at: [0, 0.75], form: { kind: "disc", r: 1.55, ry: 0.35 } },
        { color: "shell", form: { kind: "path", ops: [["M", 1.6, 0.7], ["C", 1.5, 1.9, -1.5, 1.9, -1.6, 0.7], ["Z"]] } },
        { color: "shellDark", form: { kind: "stroke", width: 0.05, ops: shellLines } },
        ...moss,
        { color: "moss", at: [-0.5, 1.5], form: { kind: "disc", r: 0.2, ry: 0.3 } },
      ],
    },
    { name: "flower", parent: "root", at: [0.3, 1.6], z: 7, moves: true, shapes: [{ color: "moss", form: { kind: "stroke", width: 0.05, ops: [["M", 0, 0], ["Q", 0.05, 0.35, 0.15, 0.6]] } }, { color: "petal", at: [0.15, 0.6], form: { kind: "disc", r: 0.12 } }] },
    {
      name: "head",
      parent: "root",
      at: [1.5, 0.75],
      z: 7,
      moves: true,
      shapes: [{ color: "skin", form: { kind: "disc", r: 0.26 } }, { color: "skin", form: { kind: "path", ops: [["M", -0.1, 0.25], ["Q", 0.7, 0.4, 0.95, 0], ["Q", 0.7, -0.3, -0.1, -0.25], ["Z"]] } }, { color: "eye", at: [0.6, 0.1], form: { kind: "disc", r: 0.07 } }],
    },
  ],
});

export function mossbackColors(id: string): Palette {
  const skin = hsl(0.06, 0.3, 0.22 + idHash(id, 4) * 0.12);
  const shell = hsl(0.1 + idHash(id, 5) * 0.05, 0.22, 0.36);
  return { skin, far: scale(skin, 0.7), shell, shellDark: scale(shell, 0.7), moss: [0.36, 0.58, 0.24], petal: [0.95, 0.75, 0.3], eye: [0.07, 0.05, 0.03] };
}

/** Plodding: six legs stepping in turn, barely a bounce; the head dips to graze and chews; the flower nods. */
export class MossbackAnimator implements Animator<Gait> {
  private t: number;
  private stride = 0;
  private dip = 0;

  constructor(seed = Math.random()) {
    this.t = seed * 10;
  }

  step(dt: number, g: Gait): Pose {
    this.t += dt;
    const walking = g.speed > 0.4;
    if (walking) this.stride += dt * g.speed * 3.2;
    const swing = walking ? Math.min(1, g.speed / 2) * 0.38 : 0;
    this.dip += ((g.eating ? 1 : 0) - this.dip) * (1 - Math.exp(-dt * 4));
    const pose: Record<string, { rot?: number; y?: number }> = {
      root: { y: walking ? Math.abs(Math.sin(this.stride)) * 0.03 : 0 },
      head: { rot: -this.dip * 0.6 + (g.eating ? Math.sin(this.t * 6) * 0.05 : Math.sin(this.t * 0.7) * 0.04) },
      flower: { rot: Math.sin(this.t * 1.5) * 0.06 },
    };
    for (let i = 0; i < 6; i++) pose[`leg${i}`] = { rot: Math.sin(this.stride * 0.8 + i * 1.05) * swing };
    return pose;
  }
}
